import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { USER_PLAN_SERVICE } from "utils/CommonConstants";
import { responseTypes } from "utils/Types";
import { axiosDelete, axiosGet, axiosPatch, axiosPost, axiosPut } from "../../../utils/APIHelper";
import { MyPlanDetail, MyPlanDetailState } from "./MyPlanDetail";

const initialState: MyPlanDetailState = {
  myPlanDetails: {},
  status: "idle",
  ad: [],
  owner: {},
  userDetail: [],
  isUserDetailLoading: true,
  isRedirectFromNotification: false,
  commentUsers: [],
  planMembers: [],
  myPlanDetailShowLoader: false,
  userValidationCheckLoader: false
};

export const addToMyPlansAsync = createAsyncThunk("plans/addToMyPlans", async (data: {}, thunkApi) => {
  const API_URL = "plans";
  const response: any = await axiosPost(API_URL, "userplan", data);
  if (response.response?.status === responseTypes.error) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchMyPlanDetailAsync = createAsyncThunk("myPlanDetails", async (data: any, thunkApi) => {
  const API_URL = `plans/${data?.id}?includePropertyInfo=${data?.includePropertyInfo || false}`;
  const response: any = await axiosGet(API_URL, "userplan");
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchAutoCompleteAsync = createAsyncThunk("autoComplete", async (data: any, thunkApi) => {
  const API_URL = `associates/?page=${data.page}&size=${data.size}&searchText=${data.value}&innCodes=${
    data?.innCode || ""
  }`;
  const response: any = await axiosGet(API_URL, "user");
  if (
    (response?.response?.status >= responseTypes.internal_error &&
      response?.response?.status < responseTypes.other_error) ||
    response?.response?.status === responseTypes.not_found
  ) {
    return thunkApi.rejectWithValue(response.response);
  }
  return response.data;
});

export const createSectionAsync = createAsyncThunk("myPlanSection", async (data: any, thunkApi) => {
  const API_URL = `plans/${data.planId}/sections`;
  const response: any = await axiosPost(API_URL, "userplan", { ...data });
  return response.data;
});

export const markPlanCompleteAsync = createAsyncThunk("myPlanComplete", async (data: any, thunkApi) => {
  const API_URL = `plans/${data?.planId}/bulk-action`;
  const response: any = await axiosPut(API_URL, USER_PLAN_SERVICE, { ...data?.payload });
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  response.data.isActionHighlight = data.isActionHighlight;
  return response.data;
});

export const removeMemberBulkActionAsync = createAsyncThunk("bulkAction", async (data: any, thunkApi) => {
  const API_URL = `plans/${data?.planId}/bulk-action`;
  const response: any = await axiosPut(API_URL, USER_PLAN_SERVICE, { ...data?.payload });
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const editSectionAsync = createAsyncThunk("editPlanSection", async (data: any, thunkApi) => {
  const API_URL = `plans/${data.planId}/sections/${data.sectionId}`;
  const response: any = await axiosPatch(API_URL, "userplan", { ...data });
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const deleteSectionAsync = createAsyncThunk("deletePlanSection", async (data: any, thunkApi) => {
  const API_URL = `plans/${data.planId}/sections/${data.sectionId}`;
  const { dispatch, fulfillWithValue, rejectWithValue } = thunkApi;
  const response: any = await axiosDelete(API_URL, USER_PLAN_SERVICE);
  if (response.status === responseTypes.no_content) {
    dispatch(fetchMyPlanDetailAsync({ id: data.planId, includePropertyInfo: true }));
    return fulfillWithValue(data);
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const updateMyPlanCardDetailAsync = createAsyncThunk("myPlanCardDetails", async (data: any, thunkApi) => {
  const API_URL = `plans/${data?.url}`;
  const response: any = await axiosPatch(API_URL, "userplan", data.payload);
  if (response.status === responseTypes?.success) return response.data;
  else return thunkApi.rejectWithValue(response.response.data);
});

export const fetchUserRemoveValidationAsync = createAsyncThunk(`userRemovalValidation`, async (data: any, thunkApi) => {
  const API_URL = `plans/${data?.planId}/validate-user-removal/${data?.hltId}`;
  const response: any = await axiosGet(API_URL, USER_PLAN_SERVICE);
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchAssigneeDetailsAsync = createAsyncThunk(`assigneeDetails`, async (data: any, thunkApi) => {
  const API_URL = `associates?size=${data.size}&hltIds=${data.value}`;
  const response: any = await axiosGet(API_URL, "user");
  return response.data;
});

export const fetchPlanMembersAsync = createAsyncThunk(`planMembers`, async (data: any, thunkApi) => {
  const API_URL = `plans/${data.planId}/plan-members`;
  const response: any = await axiosGet(API_URL, "userplan");
  return response.data;
});

export const fetchCommentUsersAsync = createAsyncThunk(`commentsUsers`, async (data: any, thunkApi) => {
  const API_URL = `associates?size=${data.size}&hltIds=${data.value}`;
  const response: any = await axiosGet(API_URL, "user");
  return response.data;
});

export const patchReorderSectionsAsync = createAsyncThunk("reorderSections", async (data: any, thunkApi) => {
  const API_URL = `plans/${data.id}`;
  const response: any = await axiosPatch(API_URL, "userplan", data.reorderedSections);
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchUserByInncode = createAsyncThunk(`edit/list`, async (data: any, thunkApi) => {
  const API_URL = `associates?size=${data.size}&hltIds=${data.value}`;
  const response: any = await axiosGet(API_URL, "user");
  return response.data;
});

export const patchAddInviteeAsync = createAsyncThunk("addInvitee", async (data: any, thunkApi) => {
  const API_URL = `plans/${data.id}`;
  const response: any = await axiosPatch(API_URL, "userplan", data.inviteeList);
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const patchUpdateOwnershipAsync = createAsyncThunk("updateOwnership", async (data: any, thunkApi) => {
  const API_URL = `plans/${data.id}`;
  const response: any = await axiosPatch(API_URL, "userplan", data.ownersList);
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const updatePlanAsync = createAsyncThunk("editPlan", async (data: any, thunkApi) => {
  const API_URL = `plans/${data?.url}`;
  const response: any = await axiosPatch(API_URL, "userplan", data.payload);
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const deleteActionCardAsync = createAsyncThunk(`myplans_deleteActionCard`, async (data: any, thunkApi) => {
  const { dispatch, rejectWithValue } = thunkApi;
  const { planId, sectionId, cardId, cardEditable } = data;
  const cardIds = cardId.join(",");
  const API_URL = `plans/${planId}/sections/${sectionId}/cards?cardId=${cardIds}`;
  const response = await axiosDelete(API_URL, "userplan");

  if (response.status === responseTypes?.success || response.status === responseTypes?.deleted) {
    dispatch(fetchMyPlanDetailAsync({ id: planId, includePropertyInfo: true }));
    return { data, cardEditable };
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const copyActionAsync = createAsyncThunk("copyAction", async (data: any, thunkApi) => {
  const { planId, sectionId } = data.url;
  const API_URL = `plans/${planId}/sections/${sectionId}/cards`;
  const response: any = await axiosPost(API_URL, "userplan", data.payload);
  if (response.response?.status === responseTypes.error) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const MyPlanDetailSlice = createSlice({
  name: "plan details",
  initialState,
  reducers: {
    clearUserDetail: (state) => {
      state.userDetail = [];
      state.isUserDetailLoading = true;
    },
    setIsRedirectFromNotification: (state, action) => {
      state.isRedirectFromNotification = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMyPlanDetailAsync.pending, (state) => {
        state.myPlanDetailShowLoader = true;
        state.status = "loading";
      })
      .addCase(fetchMyPlanDetailAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.status = "idle";
        state.ad = [];
        state.myPlanDetails = action.payload;
        state.myPlanDetailShowLoader = false;
      })
      .addCase(fetchMyPlanDetailAsync.rejected, (state) => {
        state.status = "failed";
        state.myPlanDetailShowLoader = false;
      })
      .addCase(createSectionAsync.pending, (state) => {
        state.status = "loading";
        state.myPlanDetailShowLoader = true;
      })
      .addCase(createSectionAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.status = "idle";
        state.myPlanDetailShowLoader = false;
      })
      .addCase(createSectionAsync.rejected, (state) => {
        state.status = "failed";
        state.myPlanDetailShowLoader = false;
      })
      .addCase(markPlanCompleteAsync.pending, (state, action) => {
        if (action?.meta?.arg?.type !== "bulk_assign") state.myPlanDetailShowLoader = true;
      })
      .addCase(markPlanCompleteAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetailShowLoader = false;
        state.myPlanDetails = action.payload;
      })
      .addCase(markPlanCompleteAsync.rejected, (state) => {
        state.myPlanDetailShowLoader = false;
      })
      .addCase(editSectionAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editSectionAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.status = "idle";
        state.myPlanDetails = action.payload;
      })
      .addCase(fetchAssigneeDetailsAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAssigneeDetailsAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.ad = action.payload?.users;
        if (state.myPlanDetails.owners?.length) {
          state.owner = state.myPlanDetails.owners?.map((item: any) =>
            action.payload?.users.find((user: any) => user.hltId === item)
          );
        }
        state.isUserDetailLoading = false;
      })
      .addCase(fetchAssigneeDetailsAsync.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchPlanMembersAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPlanMembersAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.planMembers = action.payload?.planMembers;
      })
      .addCase(fetchUserRemoveValidationAsync.pending, (state) => {
        state.userValidationCheckLoader = true;
      })
      .addCase(fetchUserRemoveValidationAsync.fulfilled, (state, action) => {
        state.userValidationCheckLoader = false;
      })
      .addCase(fetchUserRemoveValidationAsync.rejected, (state, action) => {
        state.userValidationCheckLoader = false;
      })
      .addCase(removeMemberBulkActionAsync.pending, (state) => {
        state.userValidationCheckLoader = true;
      })
      .addCase(removeMemberBulkActionAsync.fulfilled, (state, action) => {
        state.myPlanDetails = action?.payload;
        state.userValidationCheckLoader = false;
      })
      .addCase(removeMemberBulkActionAsync.rejected, (state, action) => {
        state.userValidationCheckLoader = false;
      })
      .addCase(fetchCommentUsersAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.commentUsers = action?.payload?.users;
      })
      .addCase(fetchCommentUsersAsync.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchAutoCompleteAsync.pending, (state, action) => {
        state.isUserDetailLoading = true;
      })
      .addCase(fetchAutoCompleteAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.isUserDetailLoading = false;
        state.userDetail = action?.payload?.users;
      })
      .addCase(fetchAutoCompleteAsync.rejected, (state, action) => {
        state.status = "idle";
        state.isUserDetailLoading = false;
        state.userDetail = [];
      })
      .addCase(patchReorderSectionsAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetails = action.payload;
      })
      .addCase(patchAddInviteeAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetails = action.payload;
      })

      .addCase(patchUpdateOwnershipAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetails = action.payload;
      })
      .addCase(updatePlanAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetails = action.payload;
      })
      .addCase(updateMyPlanCardDetailAsync.fulfilled, (state, action: any) => {
        state.myPlanDetails = action.payload.userPlanDetail;
      })
      .addCase(copyActionAsync.fulfilled, (state, action: any) => {
        state.myPlanDetails = action.payload.userPlanDetail;
      })
      .addCase(addToMyPlansAsync.fulfilled, (state, action: any) => {
        state.myPlanDetails = action.payload;
      });
  }
});

export const { clearUserDetail, setIsRedirectFromNotification } = MyPlanDetailSlice.actions;

export default MyPlanDetailSlice.reducer;
